import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import CreerPatientModal from '../../../components/feature/CreerPatientModal';

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface Disponibilite {
  id: string;
  medecin_id: string;
  jour_semaine: number;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
}

interface PatientExistant {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  telephone?: string;
  email?: string;
  numero_dossier?: string;
}

export default function SecretaireNouveauRendezVousPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [selectedMedecin, setSelectedMedecin] = useState('');
  const [disponibilites, setDisponibilites] = useState<Disponibilite[]>([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCreerPatientModal, setShowCreerPatientModal] = useState(false);
  const [patientExistant, setPatientExistant] = useState<PatientExistant | null>(null);
  const [checkingPatient, setCheckingPatient] = useState(false);
  const [dossierNotFound, setDossierNotFound] = useState(false);
  const [rendezVousUrgentsActive, setRendezVousUrgentsActive] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [isHorsJourTravail, setIsHorsJourTravail] = useState(false);

  const [formData, setFormData] = useState({
    numero_dossier: '',
    date_rdv: '',
    heure_debut: '',
    heure_fin: '',
    motif: ''
  });

  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const { mutate, state: mutationState } = useOfflineMutation<any>();
  const isOnlineRef = useRef(navigator.onLine);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'secretaire' && role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }

    loadMedecins();
    loadSystemConfig();
  }, [user, role, authLoading, navigate]);

  useEffect(() => {
    if (selectedMedecin) {
      loadDisponibilites(selectedMedecin);
    } else {
      setDisponibilites([]);
      setIsHorsJourTravail(false);
    }
  }, [selectedMedecin]);

  useEffect(() => {
    if (selectedMedecin && formData.date_rdv && disponibilites.length > 0) {
      verifierJourTravail();
    } else {
      setIsHorsJourTravail(false);
    }
  }, [formData.date_rdv, selectedMedecin, disponibilites]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const dossier = formData.numero_dossier.trim();
      if (dossier) {
        searchPatientByDossier(dossier);
      } else {
        setPatientExistant(null);
        setSelectedPatientId(null);
        setDossierNotFound(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.numero_dossier]);

  const loadSystemConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'rdv')
        .single();

      if (!error && data?.config_value) {
        const config = data.config_value as Record<string, unknown>;
        if (config.rendez_vous_urgents_active === true) {
          setRendezVousUrgentsActive(true);
        }
      }
    } catch (err) {
      console.error('Erreur chargement config RDV:', err);
    }
  };

  const verifierJourTravail = () => {
    if (!formData.date_rdv) {
      setIsHorsJourTravail(false);
      return;
    }
    const dateObj = new Date(formData.date_rdv + 'T00:00:00');
    const jourSemaine = dateObj.getDay();

    const dispoJour = disponibilites.find(
      d => d.jour_semaine === jourSemaine && d.actif
    );

    setIsHorsJourTravail(!dispoJour);
    if (dispoJour) {
      setIsUrgent(false);
    }
  };

  const loadMedecins = async () => {
    const { data, error } = await supabase
      .from('medecins')
      .select('id, nom, prenom, specialite')
      .order('nom');

    if (!error && data) {
      setMedecins(data);
    }
  };

  const loadDisponibilites = async (medecinId: string) => {
    const { data, error } = await supabase
      .from('disponibilites_medecins')
      .select('*')
      .eq('medecin_id', medecinId)
      .eq('actif', true)
      .order('jour_semaine');

    if (!error && data) {
      setDisponibilites(data);
    }
  };

  const searchPatientByDossier = async (dossier: string) => {
    setCheckingPatient(true);
    setDossierNotFound(false);

    const { data, error } = await supabase
      .from('patients')
      .select('id, nom, prenom, date_naissance, telephone, email, numero_dossier')
      .ilike('numero_dossier', dossier)
      .maybeSingle();

    if (!error && data) {
      setPatientExistant(data);
      setSelectedPatientId(data.id);
      setDossierNotFound(false);
    } else {
      setPatientExistant(null);
      setSelectedPatientId(null);
      if (dossier.length >= 2) {
        setDossierNotFound(true);
      } else {
        setDossierNotFound(false);
      }
    }
    setCheckingPatient(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isHorsJourTravail && !isUrgent && rendezVousUrgentsActive) {
      return;
    }

    if (!selectedPatientId) {
      return;
    }

    if (patientExistant && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    await createRendezVous(selectedPatientId);
  };

  const createRendezVous = async (patientId: string) => {
    setLoading(true);

    const online = navigator.onLine;
    isOnlineRef.current = online;

    const now = new Date();
    const appointmentDateTime = new Date(formData.date_rdv + 'T' + formData.heure_debut);
    if (appointmentDateTime <= now) {
      alert('Impossible de créer un rendez-vous dans le passé. Veuillez choisir une date et heure futures.');
      setLoading(false);
      return;
    }

    try {
      // Vérifications anti-double-booking (seulement en ligne)
      if (online) {
      const { data: existingRdv, error: checkError } = await supabase
        .from('rendez_vous')
        .select('id')
        .eq('medecin_id', selectedMedecin)
        .eq('date_rdv', formData.date_rdv)
        .eq('heure_debut', formData.heure_debut)
        .neq('statut', 'annule')
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingRdv) {
        throw new Error('Ce créneau horaire est déjà réservé pour ce médecin. Veuillez choisir une autre date ou heure.');
      }

      const { data: overlappingRdv, error: overlapError } = await supabase
        .from('rendez_vous')
        .select('id, heure_debut, heure_fin')
        .eq('medecin_id', selectedMedecin)
        .eq('date_rdv', formData.date_rdv)
        .neq('statut', 'annule');

      if (overlapError) {
        throw overlapError;
      }

      if (overlappingRdv && overlappingRdv.length > 0) {
        const newStart = formData.heure_debut;
        const newEnd = formData.heure_fin;

        const hasOverlap = overlappingRdv.some((rdv) => {
          const existingStart = rdv.heure_debut;
          const existingEnd = rdv.heure_fin;

          return (
            (newStart >= existingStart && newStart < existingEnd) ||
            (newEnd > existingStart && newEnd <= existingEnd) ||
            (newStart <= existingStart && newEnd >= existingEnd)
          );
        });

        if (hasOverlap) {
          throw new Error('Ce créneau horaire chevauche un rendez-vous existant. Veuillez choisir une autre heure.');
        }
      }
      } // fin if (online)

      const typeRdv = isUrgent || isHorsJourTravail ? 'urgent' : 'normal';

      const rdvData = {
        patient_id: patientId,
        medecin_id: selectedMedecin,
        date_rdv: formData.date_rdv,
        heure_debut: formData.heure_debut,
        heure_fin: formData.heure_fin,
        motif: formData.motif || null,
        statut: 'en_attente',
        type_rdv: typeRdv
      };

      const medecin = medecins.find(m => m.id === selectedMedecin);
      const medecinName = medecin ? `Dr ${medecin.nom} ${medecin.prenom}` : 'ce médecin';

      // Mutation via le hook offline-first
      await mutate({
        table: 'rendez_vous',
        operation: 'INSERT',
        data: rdvData,
        metadata: {
          summary: `RDV ${medecinName} - ${formData.date_rdv} ${formData.heure_debut}`,
          performedBy: role || 'secretaire',
        },
        onlineExecutor: async () => {
          const { data: inserted, error: rdvError } = await supabase
            .from('rendez_vous')
            .insert(rdvData)
            .select()
            .single();

          if (rdvError) {
            if (rdvError.code === '23505') {
              throw new Error('Ce créneau horaire est déjà réservé pour ce médecin.');
            }
            throw rdvError;
          }
          return inserted;
        },
      });

      // Notifications (seulement en ligne)
      if (online) {
      try {
        const medecin = medecins.find(m => m.id === selectedMedecin);
        const { data: medecinData } = await supabase
          .from('medecins')
          .select('email, telephone, nom, prenom, user_id')
          .eq('id', selectedMedecin)
          .single();

        const { data: patientData } = await supabase
          .from('patients')
          .select('email, telephone, nom, prenom')
          .eq('id', patientId)
          .single();

        const dateFormatted = new Date(formData.date_rdv + 'T00:00:00').toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });

        const prefixUrgent = typeRdv === 'urgent' ? '🔴 URGENT - ' : '';

        if (patientData) {
          const patientName = `${patientData.prenom} ${patientData.nom}`;
          const medecinFullName = medecinData ? `Dr ${medecinData.nom} ${medecinData.prenom}` : (medecin ? `Dr ${medecin.nom} ${medecin.prenom}` : 'votre médecin');
          const specialite = medecin?.specialite || '';

          if (patientData.email) {
            await supabase.functions.invoke('send-email', {
              body: {
                to: patientData.email,
                toName: patientName,
                subject: prefixUrgent + 'Votre rendez-vous est confirme - Clinique Sainte-Famille',
                htmlContent: '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:40px;font-family:Arial,sans-serif;background:#f5f5f5;"><div style="max-width:600px;margin:0 auto;background:white;padding:40px;border-radius:10px;">' + (typeRdv === 'urgent' ? '<div style="background:#FEE2E2;border-left:4px solid #DC2626;padding:15px;margin-bottom:20px;border-radius:8px;"><p style="margin:0;color:#991B1B;font-weight:bold;font-size:16px;">Rendez-vous URGENT</p></div>' : '') + '<h1 style="color:#10B981;margin-bottom:20px;">Rendez-vous confirme</h1><p>Bonjour <strong>' + patientName + '</strong>,</p><p>Votre rendez-vous a ete enregistre avec succes a la Clinique Sainte-Famille.</p><div style="background:#f9fafb;border-left:4px solid #10B981;padding:20px;margin:20px 0;border-radius:5px;"><p style="margin:5px 0;"><strong>Medecin :</strong> ' + medecinFullName + '</p>' + (specialite ? '<p style="margin:5px 0;"><strong>Specialite :</strong> ' + specialite + '</p>' : '') + '<p style="margin:5px 0;"><strong>Date :</strong> ' + dateFormatted + '</p><p style="margin:5px 0;"><strong>Heure :</strong> ' + formData.heure_debut + '</p>' + (formData.motif ? '<p style="margin:5px 0;"><strong>Motif :</strong> ' + formData.motif + '</p>' : '') + '</div><p style="color:#666;font-size:14px;">Vous recevrez un rappel 24h avant votre rendez-vous.</p><hr style="margin:30px 0;border:none;border-top:1px solid #eee;"><p style="color:#999;font-size:12px;text-align:center;">Clinique Sainte-Famille</p></div></body></html>'
              }
            });
          } else if (patientData.telephone) {
            await supabase.functions.invoke('send-sms', {
              body: {
                to: patientData.telephone,
                message: prefixUrgent + 'Clinique Sainte-Famille : RDV confirme avec ' + medecinFullName + (specialite ? ' (' + specialite + ')' : '') + ' le ' + dateFormatted + ' a ' + formData.heure_debut + '.'
              }
            });
          }

          if (medecinData?.email) {
            await supabase.functions.invoke('send-email', {
              body: {
                to: medecinData.email,
                toName: medecinFullName,
                subject: prefixUrgent + 'Nouveau rendez-vous - ' + patientName,
                htmlContent: '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:40px;font-family:Arial,sans-serif;background:#f5f5f5;"><div style="max-width:600px;margin:0 auto;background:white;padding:40px;border-radius:10px;">' + (typeRdv === 'urgent' ? '<div style="background:#FEE2E2;border-left:4px solid #DC2626;padding:15px;margin-bottom:20px;border-radius:8px;"><p style="margin:0;color:#991B1B;font-weight:bold;font-size:16px;">Rendez-vous URGENT - Hors jour de travail habituel</p></div>' : '') + '<h1 style="color:#3b82f6;margin-bottom:20px;">Nouveau rendez-vous</h1><p>Bonjour <strong>' + medecinFullName + '</strong>,</p><p>Un nouveau rendez-vous a ete programme :</p><div style="background:#f9fafb;border-left:4px solid #3b82f6;padding:20px;margin:20px 0;border-radius:5px;"><p style="margin:5px 0;"><strong>Patient :</strong> ' + patientName + '</p>' + (patientData.telephone ? '<p style="margin:5px 0;"><strong>Telephone :</strong> ' + patientData.telephone + '</p>' : '') + '<p style="margin:5px 0;"><strong>Date :</strong> ' + dateFormatted + '</p><p style="margin:5px 0;"><strong>Heure :</strong> ' + formData.heure_debut + '</p>' + (formData.motif ? '<p style="margin:5px 0;"><strong>Motif :</strong> ' + formData.motif + '</p>' : '') + '</div><hr style="margin:30px 0;border:none;border-top:1px solid #eee;"><p style="color:#999;font-size:12px;text-align:center;">Clinique Sainte-Famille</p></div></body></html>'
              }
            });
          } else if (medecinData?.telephone) {
            await supabase.functions.invoke('send-sms', {
              body: {
                to: medecinData.telephone,
                message: prefixUrgent + 'Clinique Sainte-Famille : Nouveau RDV avec ' + patientName + ' le ' + dateFormatted + ' a ' + formData.heure_debut + (formData.motif ? ' (' + formData.motif + ')' : '') + '.'
              }
            });
          }

          if (medecinData?.user_id) {
            await supabase.functions.invoke('send-notification', {
              body: {
                user_id: medecinData.user_id,
                type: 'rdv_urgent',
                titre: typeRdv === 'urgent' ? 'Nouveau rendez-vous URGENT' : 'Nouveau rendez-vous',
                message: 'RDV ' + (typeRdv === 'urgent' ? 'urgent ' : '') + 'avec ' + patientName + ' le ' + dateFormatted + ' a ' + formData.heure_debut,
                lien: '/medecin/rendez-vous',
                metadata: { rdv_type: typeRdv, date_rdv: formData.date_rdv }
              }
            });
          }
        }
      } catch (notifError) {
        console.error('Erreur envoi notifications RDV:', notifError);
      }
      } // fin if (online)

      const msg = online
        ? (typeRdv === 'urgent' ? 'Rendez-vous URGENT créé' : 'Rendez-vous créé') + ' avec succès ! Les confirmations ont été envoyées.'
        : 'Rendez-vous enregistré en mode hors-ligne. Il sera synchronisé automatiquement au retour de la connexion.';

      alert(msg);
      navigate('/secretaire/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Erreur:', error);
      const errorMessage = error.message || 'Une erreur est survenue lors de la création du rendez-vous';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPatient = async (isCorrectPatient: boolean) => {
    if (isCorrectPatient && patientExistant) {
      setShowConfirmation(false);
      await createRendezVous(patientExistant.id);
    } else {
      setPatientExistant(null);
      setSelectedPatientId(null);
      setShowConfirmation(false);
      setFormData({ ...formData, numero_dossier: '' });
      setDossierNotFound(false);
    }
  };

  const handlePatientCreated = async (patientId: string) => {
    setSelectedPatientId(patientId);
    await createRendezVous(patientId);
  };

  const JOURS_SEMAINE = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi'
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/secretaire/dashboard')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Nouveau rendez-vous</h1>
              <p className="mt-2 text-gray-600">Créer un rendez-vous pour un patient</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-clinic-blue-dark mb-2">Identification du patient</h2>
              <p className="text-gray-600">Saisissez le numéro de dossier pour identifier le patient</p>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Numéro de dossier <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={formData.numero_dossier}
                  onChange={(e) => setFormData({ ...formData, numero_dossier: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm uppercase tracking-wider"
                  placeholder="Ex: CSF1393"
                  autoComplete="off"
                />
                {checkingPatient && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <i className="ri-loader-4-line text-xl text-blue-600 animate-spin"></i>
                  </div>
                )}
              </div>
            </div>

            {checkingPatient && (
              <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl flex items-center gap-3">
                <i className="ri-loader-4-line text-xl text-blue-600 animate-spin"></i>
                <p className="text-blue-800 font-medium">Recherche du patient...</p>
              </div>
            )}

            {dossierNotFound && !checkingPatient && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                  <div>
                    <p className="text-red-800 font-semibold mb-1">Aucun patient trouvé</p>
                    <p className="text-red-700 text-sm">
                      Aucun patient avec le numéro de dossier <strong>{formData.numero_dossier}</strong> n'a été trouvé. 
                      Vérifiez le numéro ou créez d'abord le patient.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {patientExistant && !checkingPatient && (
              <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-green-600"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-green-900 mb-3">Patient identifié</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-white p-3 rounded-xl">
                        <p className="text-xs text-gray-500 mb-0.5">Nom complet</p>
                        <p className="font-bold text-gray-900 text-lg">
                          {patientExistant.prenom} <span className="uppercase">{patientExistant.nom}</span>
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <p className="text-xs text-gray-500 mb-0.5">Numéro de dossier</p>
                        <p className="font-bold text-gray-900 text-lg">{patientExistant.numero_dossier}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <p className="text-xs text-gray-500 mb-0.5">Date de naissance</p>
                        <p className="font-semibold text-gray-900">
                          {new Date(patientExistant.date_naissance + 'T00:00:00').toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      {patientExistant.telephone && (
                        <div className="bg-white p-3 rounded-xl">
                          <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                          <p className="font-semibold text-gray-900">{patientExistant.telephone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!patientExistant && !checkingPatient && !dossierNotFound && (
              <div className="mb-8 p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl flex items-center gap-3">
                <i className="ri-search-line text-xl text-gray-400"></i>
                <p className="text-gray-500 text-sm">
                  Saisissez un numéro de dossier pour rechercher le patient
                </p>
              </div>
            )}

            <div className="mb-8 pt-8 border-t-2 border-gray-100">
              <h2 className="text-2xl font-bold text-clinic-blue-dark mb-2">Informations du rendez-vous</h2>
              <p className="text-gray-600">Sélectionnez le médecin et l'horaire</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Médecin <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={selectedMedecin}
                  onChange={(e) => setSelectedMedecin(e.target.value)}
                  className="w-full px-4 py-3 pr-8 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm cursor-pointer"
                >
                  <option value="">Sélectionner un médecin</option>
                  {medecins.map((medecin) => (
                    <option key={medecin.id} value={medecin.id}>
                      Dr. {medecin.nom} {medecin.prenom} - {medecin.specialite}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date du rendez-vous <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  value={formData.date_rdv}
                  onChange={(e) => setFormData({ ...formData, date_rdv: e.target.value })}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-sm ${
                    isHorsJourTravail
                      ? 'border-red-300 focus:border-red-500 bg-red-50'
                      : 'border-clinic-blue/20 focus:border-clinic-blue'
                  }`}
                />
                {isHorsJourTravail && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <i className="ri-alert-line"></i>
                    {JOURS_SEMAINE[new Date(formData.date_rdv + 'T00:00:00').getDay()]} n'est pas un jour de travail habituel pour ce médecin
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure de début <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  required
                  value={formData.heure_debut}
                  onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure de fin <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  required
                  value={formData.heure_fin}
                  onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Motif de consultation
                </label>
                <textarea
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
                  placeholder="Décrivez le motif de la consultation..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.motif.length}/500 caractères</p>
              </div>
            </div>

            {isHorsJourTravail && rendezVousUrgentsActive && (
              <div className="mb-8 p-5 bg-red-50 border-2 border-red-300 rounded-2xl">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-fill text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-red-800 mb-1">Attention : Jour non travaillé</h4>
                    <p className="text-red-700 text-sm">
                      Ce médecin ne travaille habituellement pas le {JOURS_SEMAINE[new Date(formData.date_rdv + 'T00:00:00').getDay()]}. 
                      Cochez la case ci-dessous pour confirmer qu'il s'agit d'un rendez-vous urgent.
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-3 p-3 bg-white border-2 border-red-200 rounded-xl cursor-pointer hover:border-red-400 transition-all">
                  <input
                    type="checkbox"
                    checked={isUrgent}
                    onChange={(e) => setIsUrgent(e.target.checked)}
                    className="w-5 h-5 text-red-600 border-red-300 rounded focus:ring-red-500 cursor-pointer"
                  />
                  <div>
                    <span className="font-semibold text-red-800">Marquer comme rendez-vous URGENT</span>
                    <p className="text-xs text-red-600">Le médecin sera notifié qu'il s'agit d'une urgence</p>
                  </div>
                </label>
              </div>
            )}

            {isHorsJourTravail && !rendezVousUrgentsActive && (
              <div className="mb-8 p-5 bg-red-50 border-2 border-red-300 rounded-2xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-forbid-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-red-800 mb-1">Impossible de créer ce rendez-vous</h4>
                    <p className="text-red-700 text-sm">
                      Le {JOURS_SEMAINE[new Date(formData.date_rdv + 'T00:00:00').getDay()]} n'est pas un jour de travail pour ce médecin. 
                      Les rendez-vous urgents hors jour de travail ne sont pas activés. Contactez l'administrateur pour activer cette fonctionnalité.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isUrgent && isHorsJourTravail && (
              <div className="mb-8 p-4 bg-orange-50 border-2 border-orange-200 rounded-2xl flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-fill text-xl text-orange-600"></i>
                </div>
                <div>
                  <p className="font-semibold text-orange-800">Rendez-vous urgent confirmé</p>
                  <p className="text-orange-700 text-sm">Ce RDV sera créé en mode urgent et le médecin recevra une notification prioritaire.</p>
                </div>
              </div>
            )}

            {disponibilites.length > 0 && (
              <div className="mb-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                <h3 className="text-lg font-bold text-blue-900 mb-4">Disponibilités du médecin</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {disponibilites.map((dispo) => (
                    <div key={dispo.id} className="flex items-center gap-3 p-3 bg-white rounded-xl">
                      <i className="ri-calendar-check-line text-blue-600"></i>
                      <div>
                        <p className="font-semibold text-gray-900">{JOURS_SEMAINE[dispo.jour_semaine]}</p>
                        <p className="text-sm text-gray-600">{dispo.heure_debut} - {dispo.heure_fin}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/secretaire/dashboard')}
                className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || !selectedPatientId || (isHorsJourTravail && !isUrgent && rendezVousUrgentsActive) || (isHorsJourTravail && !rendezVousUrgentsActive)}
                className="flex-1 px-6 py-4 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Création en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line mr-2"></i>
                    {isUrgent ? 'Créer le rendez-vous urgent' : 'Créer le rendez-vous'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showConfirmation && patientExistant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirmation du patient</h3>
              <p className="text-gray-600">Veuillez confirmer que le rendez-vous est bien pour ce patient</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl mb-6 space-y-3">
              <div>
                <p className="text-sm text-gray-600">Nom complet</p>
                <p className="font-semibold text-gray-900">{patientExistant.nom} {patientExistant.prenom}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Numéro de dossier</p>
                <p className="font-semibold text-gray-900">{patientExistant.numero_dossier}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date de naissance</p>
                <p className="font-semibold text-gray-900">
                  {new Date(patientExistant.date_naissance + 'T00:00:00').toLocaleDateString('fr-FR')}
                </p>
              </div>
              {patientExistant.email && (
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900">{patientExistant.email}</p>
                </div>
              )}
              {patientExistant.telephone && (
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-semibold text-gray-900">{patientExistant.telephone}</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleConfirmPatient(true)}
                className="w-full px-6 py-4 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-check-line mr-2"></i>
                Confirmer — c'est le bon patient
              </button>
              <button
                onClick={() => handleConfirmPatient(false)}
                className="w-full px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-2"></i>
                Ce n'est pas le bon patient
              </button>
            </div>
          </div>
        </div>
      )}

      <CreerPatientModal
        isOpen={showCreerPatientModal}
        onClose={() => setShowCreerPatientModal(false)}
        onSuccess={handlePatientCreated}
      />

      <Footer />
    </div>
  );
}